<!-- eslint-disable max-len -->
<template>
  <c-loading-area :loading="loading" :height="475">
    <v-card-title>
      {{ $t('page.manageFiling.summary.title') }}
    </v-card-title>
    <v-card-subtitle v-if="!isLocked" class="pb-0">
      {{ $t('page.manageFiling.summary.description') }}
    </v-card-subtitle>
    <v-card-text>

      <!-- filing info -->
      <summary-tab-section :title="$t('page.manageFiling.summary.section.filing')" icon="mdi-folder-open">
        <dl class="d-grid grid-5050">
          <dt>{{ $t('courtCase.court') }}</dt>
          <dd>{{ filing.court.displayName }}</dd>
          <template v-if="!isNil(caseNumber)">
            <dt>{{ $t('courtCase.caseNumber') }}</dt>
            <dd>{{ caseNumber }}</dd>
          </template>
          <template v-if="!isNil(caseTitle)">
            <dt>{{ $t('courtCase.caseTitle') }}</dt>
            <dd>{{ caseTitle }}</dd>
          </template>
          <dt>{{ $t('courtCase.caseClassification') }}</dt>
          <dd>{{ filing.courtCase.caseClassificationName }}</dd>
          <dt>{{ $t('filing.filedOnBehalfOf') }}</dt>
          <dd>
            <div v-for="f in filedOnBehalfOf" :key="f.resourceID">
              {{ getFiledByName(f) }}
            </div>
          </dd>
          <dt>{{ $t('filing.referenceNumber') }}</dt>
          <dd>{{ filing.referenceNumber }}</dd>
        </dl>
      </summary-tab-section>

      <!-- parties -->
      <summary-tab-section v-if="showParties"
                           :title="isNewCaseFiling ? $t('page.manageFiling.summary.section.parties') : $t('page.manageFiling.summary.section.newParties')" icon="mdi-account-multiple">
        <div v-for="party in parties" class="d-flex align-center mb-3" :key="party.resourceID">
          <div class="pr-3 text-right" style="flex:0 0 44%;min-width:0">
            <div class="text-truncate font-weight-medium font-95">
              {{ party.actor.sortName }}
            </div>
            <div class="text-truncate label" style="font-size:11px">
              {{ party.partySubTypeExternalName }}
            </div>
          </div>
          <template v-if="generateRepresentation(party) !== null">
            <v-divider/>
            <div class="pl-3" style="flex:0 0 44%;min-width:0">
              <div class="text-truncate font-weight-medium font-95">
                {{ generateRepresentation(party) }}
              </div>
              <div class="text-truncate label text-left" style="font-size:11px">
                {{ $t('page.caseView.partiesCard.representation') }}
              </div>
            </div>
          </template>
        </div>
        <div v-if="partyOverflowCount > 0" class="d-flex mb-3">
          <div class="pr-3 text-right" style="flex:0 0 44%;min-width:0">
            <div class="text-truncate text-minimum-gray font-italic font-weight-medium font-95">
              {{ $t('page.manageFiling.summary.moreParties', [partyOverflowCount]) }}
            </div>
          </div>
        </div>
      </summary-tab-section>

      <!-- originating court cases-->
      <summary-tab-section v-if="showOrigCourtCases"
                           :title="$t('page.manageFiling.summary.section.originatingCourtCases')" icon="mdi-bank">
        <div v-for="(origCourtCase, ccIndex) in origCourtCases" class="d-flex align-center mb-5"
             :key="origCourtCase.resourceID">
          <div class="pr-3 text-right" style="flex:0 0 44%;min-width:0">
            <div class="text-truncate font-weight-medium font-95">
              {{ origCourtCase.caseNumber }}
            </div>
            <div class="text-truncate label" style="font-size:11px">
              {{ origCourtCase.courtName }}
            </div>
          </div>
          <template v-if="hasJudgments(origCourtCase)">
            <v-divider/>
            <div class="pl-3" style="flex:0 0 44%;min-width:0">
              <div v-for="(judgment, jIndex) in origCourtCase.originatingJudgments"
                   :class="addClassForAllButLast('pb-2', origCourtCase.originatingJudgments, jIndex)"
                   :key="`cc-${ccIndex}-judgment-${jIndex}`">
                <div v-if="hasJudgmentText(judgment)" class="text-truncate font-weight-medium font-95">
                  {{ generateJudgmentText(judgment.judgeName, judgment.judgmentTypeName) }}
                </div>
                <div v-if="!isNil(judgment.judgmentDate)" class="text-truncate label text-left" style="font-size:11px">
                  {{ $cove.formatDate(judgment.judgmentDate) }}
                </div>
              </div>
            </div>
          </template>
        </div>
      </summary-tab-section>

      <!-- docket entries -->
      <summary-tab-section :title="$t('page.manageFiling.summary.section.docketEntries')" icon="mdi-file-document-multiple">
        <div v-for="de in docketEntries" class="d-flex align-center pb-3" :key="de.resourceID" style="min-width:0">
          <div class="pr-3 text-right" style="flex:0 0 44%;min-width:0">
            <div class="text-truncate font-weight-medium font-95">
              {{ de.docketEntryExternalName }}
            </div>
            <div v-if="de.lead" class="text-truncate label" style="font-size:11px;margin-bottom:-3px">
              {{ $t('filing.leadDocketEntry') }}
            </div>
            <div v-if="de.excludeFromService" class="text-truncate label" style="font-size:11px">
              {{ $t('docketEntry.excludeFromService') }}
            </div>
          </div>
          <v-divider/>
          <div class="pl-3" style="flex:0 0 44%;min-width:0;">
            <div v-for="d in de.documents" class="font-weight-medium font-95" :key="d.resourceID">
              <a :href="viewDocumentURL(d.documentLinkID)" target="_blank">
                {{ d.documentName }}
              </a>
            </div>
            <div class="label text-left" style="font-size:11px">
              {{ $t('global.documents') }}
            </div>
          </div>
        </div>
      </summary-tab-section>

      <!-- services -->
      <!--   this section displays different data based upon the state of the filing -->
      <!--   - in draft mode we show data from recipient endpoint mirroring the 'service' tab info -->
      <!--   - after submission we show persisted filing service records -->
      <!--   this is because we do not cleanup extraneous filing service records that may already be persisted until filing submission -->
      <summary-tab-section v-if="showService || showRecipients"
                           :title="$t('page.manageFiling.summary.section.service')" icon="mdi-email-fast-outline">
        <template v-if="showService">
          <div v-for="fs in filingServices" :key="fs.resourceID" class="d-flex align-center pb-3" style="min-width:0">
            <div class="pr-3 text-right" style="flex:0 0 44%;min-width:0">
              <div class="text-truncate font-weight-medium font-95">
                {{ fs.sortName }}
              </div>
              <div class="text-truncate label" style="font-size:11px">
                {{ fs.filingServiceMethod.displayName }}
              </div>
            </div>
            <template v-if="showServiceDate(fs)">
              <v-divider/>
              <div class="pl-3" style="flex:0 0 44%;min-width:0;">
                <div class="font-weight-medium font-95">
                  <span v-if="isEService(fs)">
                    {{ !isNil(fs.readDate) ? $t('page.manageFiling.summary.readOn', [$cove.formatDate(fs.readDate)]) : $t('page.manageFiling.summary.unread') }}
                  </span>
                  <span v-else>
                    {{ fs.serviceDate | formatDate }}
                  </span>
                </div>
                <div class="label text-left" style="font-size:11px">
                  {{ $t('page.manageFiling.summary.serviceDate') }}
                </div>
              </div>
            </template>
          </div>
        </template>
        <template v-else-if="showRecipients">
          <div v-for="fr in filingRecipients" :key="fr.resourceID" class="d-flex align-center pb-3" style="min-width:0">
            <div class="pr-3 text-right" style="flex:0 0 44%;min-width:0">
              <div class="text-truncate font-weight-medium font-95">
                {{ fr.recipientName }}
              </div>
              <div class="text-truncate label" style="font-size:11px">
                {{ fr.filingServiceMethodName }}
              </div>
            </div>
            <template v-if="!isNil(fr.serviceDate)">
              <v-divider/>
              <div class="pl-3" style="flex:0 0 44%;min-width:0;">
                <div class="font-weight-medium font-95">
                  {{ fr.serviceDate | formatDate }}
                </div>
                <div class="label text-left" style="font-size:11px">
                  {{ $t('page.manageFiling.summary.serviceDate') }}
                </div>
              </div>
            </template>
          </div>
        </template>
      </summary-tab-section>

      <!-- filing fees -->
      <summary-tab-section v-if="feesGrandTotal > 0" :title="$t('page.manageFiling.summary.section.fees')" icon="mdi-credit-card-outline">
        <dl class="d-grid grid-5050">
          <dt>{{ $t('page.manageFiling.summary.filingFeesTotal') }}</dt>
          <dd>
            <button type="button" class="link-button" @click="openFilingFeesPopup">
              {{ feesGrandTotal | formatCurrency }}
            </button>
          </dd>
          <dt>{{ $t('page.manageFiling.summary.filingFeesDue') }}</dt>
          <dd>{{ filingFeesDue | formatCurrency }}</dd>
          <template v-if="hasFeeWaiver">
            <dt>{{ $t('filing.feeWaiverRequest') }}</dt>
            <dd>{{ feeWaiver.displayName }}</dd>
          </template>
          <template v-if="hasReceipt">
            <dt>{{ $t('global.receipt') }}</dt>
            <dd>
              <button type="button" class="link-button" @click="viewReceipt">
                {{ $t('global.viewReceipt') }}
              </button>
            </dd>
          </template>
        </dl>

        <filing-fees-popup v-model="filingFeesPopupVisible" :filingID="filingID"/>
      </summary-tab-section>
    </v-card-text>

    <v-card-actions class="card-actions-button-panel row-800 mt-7 pt-3">
      <div v-if="!isLocked">
        <c-btn type="primary" block @click="$emit('submitFiling')">
          {{ $t('page.manageFiling.sidebar.submitFiling') }}
        </c-btn>
      </div>
    </v-card-actions>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import SummaryTabSection from '@components/support/manageFiling/SummaryTabSection';
import FilingFeesPopup from '@components/support/manageFiling/FilingFeesPopup';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';
import { generateJudgmentText } from '@/application/utility';

const filingStore = useFilingStore();

export default {
  components: { SummaryTabSection, FilingFeesPopup },
  data () {
    return {
      loading: true,
      parties: [],
      partyOverflowCount: 0,
      origCourtCases: [],
      docketEntries: [],
      filingServices: [], // used to show persisted service records (after submission)
      filingRecipients: [], // used to show potential service records (in draft mode)
      feeWaiver: null,
      filingFeesPopupVisible: false
    };
  },
  computed: {
    filingID () {
      return this.filing.resourceID;
    },
    filing () {
      return filingStore.filing;
    },
    isNewCaseFiling () {
      return filingStore.isNewCaseFiling;
    },
    filedOnBehalfOf () {
      return filingStore.getFiledOnBehalfOf;
    },
    isLocked () {
      return filingStore.isLocked;
    },
    showParties () {
      return !_.isEmpty(this.parties);
    },
    showOrigCourtCases () {
      return !_.isEmpty(this.origCourtCases);
    },
    isFilerCaseAccessible () {
      return filingStore.isFilerCaseAccessible;
    },
    caseNumber () {
      return _.defaultTo(this.filing.courtCase.caseNumber, null);
    },
    caseTitle () {
      return Filing.getFilingCaseTitle(this.isFilerCaseAccessible, this.filing.courtCase.caseTitle);
    },
    showService () {
      return this.isLocked &&
        !_.isNil(_.find(this.docketEntries, { excludeFromService: false })) &&
        !_.isEmpty(this.filingServices);
    },
    showRecipients () {
      return !this.isLocked &&
        !_.isNil(_.find(this.docketEntries, { excludeFromService: false })) &&
        !_.isEmpty(this.filingRecipients);
    },
    hasFeeWaiver () {
      return this.feeWaiver !== null;
    },
    feesGrandTotal () {
      let total = 0;
      this.docketEntries.forEach((de) => {
        total += _.defaultTo(de.feeAmount, 0);
      });

      return total;
    },
    filingFeesDue () {
      return this.hasFeeWaiver ? 0 : this.feesGrandTotal;
    },
    hasReceipt () {
      let receipt = filingStore.filing.receipt;
      return !_.isNil(receipt) && receipt.receiptStatus.resourceID !== this.CONST.RECEIPT_STATUS_PENDING;
    }
  },
  created () {
    Promise.all([this.loadParties(), this.loadOrigCourtCases(), this.loadDocketEntries(), this.loadServices(),
      this.loadFeeWaiver()])
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    generateJudgmentText,
    loadFeeWaiver () {
      let url = this.$cove.getAPI({
        name: 'api.filing',
        params: { id: this.filingID },
        query: { fields: '**,feeWaiverReason(*)' }
      });

      return axios.get(url).
        then((response) => {
          this.feeWaiver = _.get(response, 'data.feeWaiverReason', null);
        });
    },
    loadParties () {
      let displayPerType = 5;
      let involvementTypeIDs = [this.CONST.INVOLVMENT_TYPE_FILING_PARTY, this.CONST.INVOLVMENT_TYPE_RESPONDING_PARTY,
        this.CONST.INVOLVMENT_TYPE_INVOLVED_PARTY];

      let promises = involvementTypeIDs.map((involvmentTypeID) => {
        let url = this.$cove.getAPI({
          name: 'api.filing.caseParties',
          query: {
            filingID: this.filingID,
            size: displayPerType,
            sort: ['actor.sortName,asc'],
            fields: '**,actor(*),legalRepresenterMaps(*,existingLegalRepresenter(*),legalRepresenter(*,actor(*)))',
            involvementTypeExternalIdentifier: involvmentTypeID }
        });

        return axios.get(url);
      });

      return Promise.all(promises)
        .then((responses) => {
          responses.forEach((response) => {
            this.parties = this.parties.concat(_.get(response, 'data._embedded.results', []));
            this.partyOverflowCount += Math.max(response.data.page.totalElements - displayPerType, 0);
          });
        });
    },
    loadOrigCourtCases () {
      let url = this.$cove.getAPI({
        name: 'api.filing.originatingCourtCases',
        query: {
          filingID: this.filingID,
          size: this.CONST.PAGING_MAX_SIZE,
          fields: '*,originatingJudgments(*)'
        }
      });

      return axios.get(url)
        .then((response) => {
          this.origCourtCases = _.get(response, 'data._embedded.results', []);
        });
    },
    loadDocketEntries () {
      let url = this.$cove.getAPI({
        name: 'api.filing.docketEntries',
        query: {
          filingID: this.filingID,
          fields: '*,documents(*)',
          size: this.CONST.PAGING_MAX_SIZE,
          sort: ['lead,desc']
        }
      });

      return axios.get(url).
        then((response) => {
          this.docketEntries = _.get(response, 'data._embedded.results', []);
        });
    },
    loadServices () {
      // loads service information from different endpoints depending on the state of the filing.
      // see comments in the template section for more details.
      if (this.isLocked)
      {
        let url = this.$cove.getAPI({
          name: 'api.filing.services',
          query: {
            filingID: this.filingID,
            fields: '*,filingServiceMethod(*)',
            size: this.CONST.PAGING_MAX_SIZE
          }
        });

        return axios.get(url).
          then((response) => {
            this.filingServices = _.get(response, 'data._embedded.results', []);
            this.filingServices = _.sortBy(this.filingServices, 'sortName');
          });
      }
      else
      {
        let url = this.$cove.getAPI({ name: 'api.filing.recipients', params: { id: this.filingID } });
        return axios.get(url).
          then((response) => {
            this.filingRecipients = _.defaultTo(response.data, []);
            this.filingRecipients = _.sortBy(this.filingRecipients, 'recipientName');
          });
      }
    },
    viewDocumentURL (documentLinkID) {
      return this.$cove.getAPI({
        name: 'api.dms.document',
        params: { documentLinkID }
      });
    },
    getFiledByName (filedOnBehalfOf) {
      return Filing.getFiledOnBehalfOfName(filedOnBehalfOf);
    },
    generateRepresentation (party) {
      let result = null;
      let legalRepMaps = party.legalRepresenterMaps;
      if (!_.isEmpty(legalRepMaps))
      {
        let proSe = _.find(legalRepMaps, { proSe: true });
        if (proSe)
        {
          result = this.$t('global.selfRep');
        }
        else
        {
          let lrm = legalRepMaps[0];
          let possibleNames = [
            _.get(lrm, 'legalRepresenter.actor.sortName', null),
            _.get(lrm, 'existingLegalRepresenter.sortName', null)
          ];

          result = _.compact(possibleNames)[0];
        }
      }

      return result;
    },
    isEService (filingService) {
      return filingService.filingServiceMethod.resourceID === this.CONST.FILING_SERVICE_METHOD_ESERVICE;
    },
    showServiceDate (filingService) {
      let fsm = filingService.filingServiceMethod;
      return fsm.resourceID === this.CONST.FILING_SERVICE_METHOD_ESERVICE || fsm.serviceDateRequired === true;
    },
    openFilingFeesPopup () {
      this.filingFeesPopupVisible = true;
    },
    viewReceipt () {
      this.$router.push({ name: 'receipt', params: { receiptID: filingStore.filing.receipt.resourceID } });
    },
    hasJudgments (origCourtCase)
    {
      return !_.isEmpty(origCourtCase.originatingJudgments);
    },
    addClassForAllButLast (classToAdd, array, index)
    {
      return index === array.length-1 ? '':classToAdd;
    },
    hasJudgmentText (judgment)
    {
      return !_.isNil(judgment.judgeName) || !_.isNil(judgment.judgmentTypeName);
    }
  }
};
</script>